import React from 'react';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import './MyAppointments.scss';
import useMyAppointmentsVM from './useMyAppointmentsVM';
import { Loader } from 'components/Loader/Loader';
import { UserPageHeader } from 'components/UserPageHeader/UserPageHeader';
import { HistoryDetailLayout } from 'components/HistoryDetailLayout/HistoryDetailLayout';
import { NumberedHistoryCard } from 'components/NumberedHistoryCard/NumberedHistoryCard';
import { LoadMoreButton } from 'components/LoadMoreButton/LoadMoreButton';
import { EditableCommentField } from 'components/EditableCommentField/EditableCommentField';
import { ScrollToTopButton } from 'components/ScrollToTopButton/ScrollToTopButton';
import { useSelector } from 'react-redux';
import { getDatalists } from 'features/datalist';
import appointmentsIcon from 'assets/images/icons/users/menu/appointments.svg';
import historyIcon from 'assets/images/icons/users/history.svg';
import detailIcon from 'assets/images/icons/users/details.svg';
import documentIcon from 'assets/images/icons/users/document.svg';
import calendarCheckIcon from 'assets/images/icons/users/calendar-check.svg';

const formatTime = (d: string) => moment(d).format('HH:mm');

export const MyAppointments: React.FC = () => {
    const { me, items, totalCount, hasMore, loadMore, selected, setSelected, formatDate, isLoading, handleSaveEventComment, handleSaveNextMeetingPrep, isLastEvent, nextAppointment } = useMyAppointmentsVM();
    const { datalists } = useSelector(getDatalists);

    if (isLoading) return <main id="my-appointments"><Loader /></main>;

    const firstname = me?.firstname ?? '';

    return (
        <main id="my-appointments">
            <UserPageHeader icon={<img src={appointmentsIcon} alt="" />} title="Mes rendez-vous pour mon projet" />

            <HistoryDetailLayout
                historyTitle="Mon historique de rendez-vous"
                historyIcon={<img src={historyIcon} alt="" />}
                historyTop={
                    nextAppointment ? (
                        <section className="my-appointments__next">
                            <header className="history-detail-layout__section-header">
                                <span aria-hidden="true"><img src={calendarCheckIcon} alt="" /></span>
                                <h2>Mon prochain rendez-vous</h2>
                            </header>
                            <NumberedHistoryCard
                                number={totalCount + 1}
                                title={nextAppointment.title}
                                subtitle={`RDV du ${formatDate(nextAppointment.date)} à ${formatTime(nextAppointment.date)}`}
                                selected={selected?.source === nextAppointment.source && selected?.id === nextAppointment.id}
                                onClick={() => setSelected(nextAppointment)}
                            />
                        </section>
                    ) : null
                }
                detailTitle="Détail du rendez-vous"
                detailIcon={<img src={detailIcon} alt="" />}
                history={
                    <>
                        {items.length === 0 && <p>Aucun rendez-vous pour le moment.</p>}
                        {items.map((item, idx) => (
                            <NumberedHistoryCard
                                key={`${item.source}-${item.id}`}
                                number={totalCount - idx}
                                title={item.title}
                                subtitle={`RDV du ${formatDate(item.date)} à ${formatTime(item.date)}`}
                                status={item.status}
                                selected={selected?.source === item.source && selected?.id === item.id}
                                onClick={() => setSelected(item)}
                            />
                        ))}
                        {hasMore && <LoadMoreButton label="Voir les anciens rendez-vous" onClick={loadMore} />}
                    </>
                }
                detail={
                    selected ? (
                        <div className="my-appointments__detail">
                            <RdvDetail
                                item={selected}
                                firstname={firstname}
                                datalists={datalists}
                                formatDate={formatDate}
                                onSaveComment={handleSaveEventComment}
                                onSaveNextPrep={handleSaveNextMeetingPrep}
                                showNextPrep={!!isLastEvent}
                            />
                        </div>
                    ) : <p>Sélectionnez un rendez-vous pour voir son détail.</p>
                }
            />

            <ScrollToTopButton />
        </main>
    );
};

type UnifiedItem = {
    id: number;
    source: 'event' | 'appointment';
    title: string;
    date: string;
    status?: 'pending' | 'refused' | 'accepted';
    raw: any;
};

const RdvDetail: React.FC<{
    item: UnifiedItem;
    firstname: string;
    datalists: any;
    formatDate: (d: string) => string;
    onSaveComment: (v: string) => Promise<void>;
    onSaveNextPrep: (v: string) => Promise<void>;
    showNextPrep: boolean;
}> = ({ item, firstname, datalists, formatDate, onSaveComment, onSaveNextPrep, showNextPrep }) => {
    const raw = item.raw;
    const isEvent = item.source === 'event';

    const attendees = [
        ...(raw.assistants_list ?? []).map((a: any) => a.label),
        ...(raw.stakeholders_list ?? []).map((s: any) => s.label),
        ...(raw.partners_list ?? []).map((p: any) => p.label)
    ].join(', ');

    const hasInfos = !!raw.communication_type || !!raw.started_at || attendees.length > 0;
    const hasObservation = !!raw.observation;
    const documents = raw.documents ?? [];
    const hasDocuments = documents.length > 0;

    return (
        <>
            {hasInfos && (
                <div className="my-appointments__infos">
                    {raw.communication_type && (
                        <p><strong>Type de rendez-vous :</strong> {datalists?.communication_types?.[raw.communication_type] ?? '—'}</p>
                    )}
                    {raw.started_at && (
                        <p><strong>Date :</strong> {formatDate(raw.started_at)}</p>
                    )}
                    {raw.started_at && (
                        <p>
                            <strong>Heure :</strong> {formatTime(raw.started_at)}
                            {raw.ended_at ? ` – ${formatTime(raw.ended_at)}` : ''}
                        </p>
                    )}
                    {attendees.length > 0 && (
                        <p><strong>Personnes présentes :</strong> {attendees}</p>
                    )}
                </div>
            )}

            {hasObservation && (
                <>
                    <h3>Commentaire FCV :</h3>
                    <div className="my-appointments__comment" dangerouslySetInnerHTML={{ __html: sanitizeHtml(raw.observation) }} />
                </>
            )}

            {isEvent && (
                <EditableCommentField
                    label={`Commentaire réservé à ${firstname} :`}
                    value={raw.user_comment}
                    onSave={onSaveComment}
                />
            )}

            {hasDocuments && (
                <>
                    <h3>Documents liés :</h3>
                    <div className="my-appointments__documents">
                        {documents.map((doc: any) => (
                            <a key={doc.id} href={doc.view_link} target="_blank" rel="noreferrer" className="my-appointments__doc">
                                <img src={documentIcon} alt="" aria-hidden="true" /> {doc.label}
                            </a>
                        ))}
                    </div>
                </>
            )}

            {isEvent && showNextPrep && (
                <EditableCommentField
                    label="Préparation à mon prochain entretien :"
                    value={raw.user_next_meeting_preparation}
                    onSave={onSaveNextPrep}
                />
            )}
        </>
    );
};
